/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Input,
  Card,
  Button,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError, onSuccess } from 'utils/handlers';
import {
  getLiquidaciones,
  getLiquidacion,
  postLiquidacion,
  patchLiquidacion,
  deleteLiquidacion,
} from 'api/nominas/liquidaciones/liquidaciones';
import { formatReceivedValues } from 'utils/formatValues';
import { getMotivosDeLiquidaciones } from 'api/catalogos/motivosDeLiquidaciones';
import TextArea from 'antd/lib/input/TextArea';
import { getLiquidacionesConfiguracionesFiniquitos } from 'api/nominas/liquidaciones/liquidacionesConfiguracionesFiniquito';
import { getPercepciones } from 'api/nominas/percepciones/percepciones';
import { getLiquidacionesConfiguracionesIndemnizaciones } from 'api/nominas/liquidaciones/liquidacionesConfiguracionesIndemnizacion';
import {
  SLUG_PATTERN,
} from 'utils/patterns';
import {
  ESTATUS_CANCELADO,
  ESTATUS_AUTORIZADO,
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
} from '../../../utils/constants';

export const liquidacionesPermission = {
  permissionModel: 'liquidacion',
  catalogsModels: [],
  tabsModels: [],
};

// eslint-disable-next-line react/prop-types
const Liquidaciones = ({ permission }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState();
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [visibleAlert, setVisibleAlert] = useState();
  const [motivosLiquidaciones, setMotivosLiquidaciones] = useState();
  const [liquidacionesFiniquitos, setLiquidacionesFiniquitos] = useState();
  const [liquidacionesIndemnizaciones, setLiquidacionesIndemnizaciones] = useState();
  const [percepciones, setPercecpiones] = useState();
  const [aplicaFiniquito, setAplicaFiniquito] = useState();
  const [aplicaIndemnizacion, setAplicaIndemnizacion] = useState();
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [disableSave, setDisableSave] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidaciones();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _motivos_de_liquidaciones,
        _liquidaciones_finiquitos,
        _liquidaciones_indemnizaciones,
        _percepciones,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getMotivosDeLiquidaciones({ estados_globales: ESTATUS_AUTORIZADO }),
        await getLiquidacionesConfiguracionesFiniquitos({ es_finiquito: true }),
        getLiquidacionesConfiguracionesIndemnizaciones({ es_finiquito: false }),
        getPercepciones({ estados_globales: ESTATUS_AUTORIZADO }),
        getLiquidaciones(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setMotivosLiquidaciones(_motivos_de_liquidaciones);
        setLiquidacionesFiniquitos(_liquidaciones_finiquitos);
        setLiquidacionesIndemnizaciones(_liquidaciones_indemnizaciones);
        setPercecpiones(_percepciones);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setEstatusAutorizado(false);
    setDisableSave(false);
    setEstatusInactivo(false);
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickAdd = () => {
    setEstadosFiltered(estados);
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacion(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableSave(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _liquidaciones = await getLiquidacion(match.id);
    if (_liquidaciones?.estados_globales === ESTATUS_ACTIVO || _liquidaciones?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO || estado?.id === ESTATUS_ACTIVO));
    }
    if (_liquidaciones?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_liquidaciones?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    setTimeout(() => {
      if (_liquidaciones) {
        form.resetFields();
        setTimeout(() => {
          isDisabled(_liquidaciones?.estados_globales);
          setAplicaFiniquito(_liquidaciones?.aplica_finiquito);
          setAplicaIndemnizacion(_liquidaciones?.aplica_indemnizacion);
          form.setFieldsValue(formatReceivedClone(_liquidaciones));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = true) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchLiquidacion(key, values) : postLiquidacion(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        message.warn('No se puede autorizar una nómina ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchLiquidacion(key, vals);
      if (res) {
        onSuccess(res);
        const _data = await getLiquidaciones();
        setData(formatReceivedClone(_data));
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    clave: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 300,
    },
    {
      titleText: 'Motivo',
      dataIndex: 'motivo_de_liquidacion',
      key: 'motivo_de_liquidacion',
      width: 300,
      render: (id) => motivosLiquidaciones.find((motivo) => motivo.id === id)?.motivo,
    },
    {
      titleText: 'Fundamento',
      dataIndex: 'fundamento',
      key: 'fundamento',
      width: 300,
    },
    {
      titleText: 'Especificación',
      dataIndex: 'especificacion',
      key: 'especificacion',
      width: 300,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  const columnsLiquidacionesFiniquitos = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (value) => percepciones.find((percepcion) => percepcion.id === value)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  const columnsLiquidacionesIndemnizaciones = [
    {
      titleText: 'Percepción',
      dataIndex: 'percepcion',
      key: 'percepcion',
      width: 300,
      render: (value) => percepciones.find((percepcion) => percepcion.id === value)?.descripcion,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!open ? (
          <Table
            data={data}
            cols={columns}
            allowExport
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit: () => onClickEdit(),
              onClickDelete,
            }}
            disableDelete={
              data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
            }
            customActions={[
              {
                onClick: () => setConfirmationVisible(true),
                permission: { model: 'liquidacion', action: 'autorizar' },
                text: 'Autorizar',
                icon: <CheckCircleOutlined />,
                disabledBySelection: false,
                className: 'success',
                disabled: !selectedRowKeys?.length
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_CANCELADO
                  || data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_INACTIVO,
              },
            ]}
            permission={permission}
            allowExportPDF={false}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Liquidación`}
            extra={(
              <FormSubmitControls
                onFinish={() => form.submit()}
                onCancel={onCancel}
                loading={loading}
                onFinishText="Guardar y Continuar"
                allowSave={!disableSave}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={onFinish}
              scrollToFirstError
              name="general"
            >
              <Row gutter={10}>
                <Col span={8}>
                  <Form.Item
                    name="clave"
                    rules={rules.clave}
                    label="Clave"
                    hasFeedback
                  >
                    <Input
                      allowClear
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="motivo_de_liquidacion"
                    label="Motivo de Liquidación"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Select
                      dataSource={motivosLiquidaciones}
                      render={(val) => val.motivo}
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="fundamento"
                    label="Fundamento"
                    hasFeedback
                  >
                    <Input
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col span={8}>
                  <Form.Item
                    name="especificacion"
                    label="Especificación"
                    rules={rules.required}
                    hasFeedback
                  >
                    <Input
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="aplica_finiquito"
                    label="Aplica  Finiquito"
                    rules={rules.required}
                    hasFeedback
                    initialValue={false}
                  >
                    <Select
                      allowClear
                      trueFalse
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="aplica_indemnizacion"
                    label="Aplica  Indemnización"
                    rules={rules.required}
                    hasFeedback
                    initialValue={false}
                  >
                    <Select
                      allowClear
                      trueFalse
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={10}>
                <Col span={24}>
                  <Form.Item
                    name="observaciones"
                    label="Observaciones"
                    hasFeedback
                  >
                    <TextArea
                      rows={3}
                      allowClear
                      disabled={estatusInactivo || estatusAutorizado}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Col span={8}>
                <Form.Item
                  name="estados_globales"
                  label="Estatus"
                  rules={rules.estatus}
                  hasFeedback
                  initialValue={1}
                >
                  <Select
                    labelProp="descripcion"
                    keyProp="id"
                    dataSource={estadosFiltered}
                    disabled={!selectedRowKeys.length || estatusInactivo || estatusAutorizado}
                    onChange={(val) => {
                      if (val === ESTATUS_INACTIVO) {
                        setConfirmationVisibleEstatus(true);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              {selectedRowKeys.length && aplicaFiniquito ? (
                <>
                  <p style={{ fontWeight: 600, marginBottom: 0, color: '#D88F4F' }}>Finiquito</p>
                  <Table
                    data={liquidacionesFiniquitos}
                    cols={columnsLiquidacionesFiniquitos}
                    allowExport
                    permission={permission}
                  />
                </>
              ) : null}
              {selectedRowKeys.length && aplicaIndemnizacion ? (
                <>
                  <p style={{ fontWeight: 600, marginBottom: 0, color: '#D88F4F' }}>Indemnización</p>
                  <Table
                    data={liquidacionesIndemnizaciones}
                    cols={columnsLiquidacionesIndemnizaciones}
                    allowExport
                    permission={permission}
                  />
                </>
              ) : null}
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Liquidación ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar la Liquidación ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          ConfirmInactive
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          contentTitle={`Inactivar la Liquidación ${form.getFieldValue('clave')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default Liquidaciones;
